import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ShieldOff } from "lucide-react";
import { API } from "../config/api";
import { Button } from "../components/ui/button";
import DirectorAnalytics from "./DirectorAnalytics";
import AdminDashboard from "./AdminDashboard";
import DirectorTools from "../components/directors/DirectorTools";

/**
 * BM-H626 (26 Sep 2026): one Directors Lounge.
 *
 * Vanessa: "i want the directors lounge which is gated to me and rebekah, the
 * rest is noise", and yes to one Lounge with tabs. Three avatar-menu entries
 * (Directors Analytics, Director Admin, Director Dashboard) and their three
 * surfaces become the three tabs here. Access is the server's director check
 * (/user-role is_director), and every endpoint the tabs call checks it again.
 */
export const LOUNGE_TABS = [
  { key: "analytics", label: "Analytics" },
  { key: "tools", label: "Tools" },
  { key: "health", label: "Data health" },
];

export default function DirectorsLounge() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("tab");
  const tab = LOUNGE_TABS.some((t) => t.key === requested) ? requested : "analytics";
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const userId = sessionStorage.getItem("bonsai_user_id") || "";
    axios
      .get(`${API}/user-role`, { headers: { "X-Bonsai-User-Id": userId } })
      .then((res) => { if (!cancelled) setAllowed(Boolean(res.data?.is_director)); })
      .catch(() => { if (!cancelled) setAllowed(false); });
    return () => { cancelled = true; };
  }, []);

  if (allowed === null) {
    return <p className="p-6 text-sm text-txt-muted" data-testid="directors-lounge-checking">Checking access…</p>;
  }
  if (!allowed) {
    return (
      <div className="p-6" data-testid="directors-lounge-403">
        <div className="max-w-3xl mx-auto border border-navy-border rounded-md p-8 mt-12 text-center">
          <ShieldOff className="w-10 h-10 text-txt-muted mx-auto mb-3" />
          <h1 className="text-lg font-semibold mb-1">Directors only</h1>
          <p className="text-sm text-txt-muted mb-4">The Directors Lounge is restricted to the firm's directors.</p>
          <Button size="sm" variant="outline" onClick={() => navigate("/")}>Back to home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="directors-lounge">
      <h1 className="text-base font-semibold">Directors Lounge</h1>
      <div className="flex gap-1 border-b border-navy-border" role="tablist">
        {LOUNGE_TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setSearchParams({ tab: t.key })}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === t.key ? "border-teal text-teal" : "border-transparent text-txt-muted hover:text-white"}`}
            data-testid={`directors-lounge-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "analytics" && <DirectorAnalytics />}
      {tab === "tools" && <DirectorTools />}
      {tab === "health" && <AdminDashboard embedded />}
    </div>
  );
}
